import type {OperationVariables, QueryOptions, MutationOptions} from "@apollo/client"
import {isMatching, P} from "ts-pattern"
import {get} from "lodash"
import client from "./client"
import type {PBApolloError} from "./helpers/hookShared"

const gqlIsErr = isMatching({error: P.nonNullable})

async function gqlQuery<T = unknown, TVariables extends OperationVariables = OperationVariables>(
  options: Prettify<
    QueryOptions<TVariables, T> & {
      /**
       * The name of the root value on the return type, only applicable if using one root node
       * @example
       * const query = gql`{
       *   user {
       *     name
       *     email
       *   }
       * }`
       *
       * const useUser = createUserQuery(query, { key: 'user' }) // returns { name: 'blah', email: 'foo@blah' }
       */
      key?: string
    }
  >
): Promise<
  | {
      data: T
    }
  | {
      error: PBApolloError
      errorMsg: string
    }
> {
  try {
    const res = await client.query(options)
    return options.key ? {data: get(res.data, options.key)} : res
  } catch (err) {
    const e = err as PBApolloError
    return Promise.resolve({error: e, errorMsg: e.networkError?.formattedMessage ?? e.message})
  }
}
async function gqlMutation<T = unknown, TVariables extends OperationVariables = OperationVariables>(
  options: Prettify<
    MutationOptions<TVariables, T> & {
      /**
       * The name of the root value on the return type, only applicable if using one root node
       * @example
       * const mutation = gql`
       *   mutation CreateUser($input: CreateUserInput!) {
       *     createUser(input: $input) {
       *       id
       *       name
       *     }
       *   }
       * `
       *
       * const result = await gqlMutation(mutation, { key: 'createUser' }) // returns { id: '123', name: 'John' }
       */
      key?: string
    }
  >
): Promise<
  | {
      data: T
    }
  | {
      error: PBApolloError
      errorMsg: string
    }
> {
  try {
    const res = (await client.mutate(options as any)) as any
    return options.key ? {data: get(res.data, options.key)} : res
  } catch (err) {
    const e = err as PBApolloError
    return Promise.resolve({error: e, errorMsg: e.networkError?.formattedMessage ?? e.message})
  }
}

export const gqlService = {
  isErr: gqlIsErr,
  query: gqlQuery,
  mutation: gqlMutation
}
